import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import { PhoneAndroid, Computer, Tablet } from '@mui/icons-material';
import { isMobileDevice, isTabletDevice, getDeviceType, shouldUseMobileUI } from '../../utils/mobileDetection';
import { testMobileDetection, testScreenSizes } from '../../utils/mobileDetectionTest';

/**
 * Demo component to test mobile detection and redirection functionality
 */
const MobileDetectionDemo = () => {
  const [detectionResults, setDetectionResults] = useState(null);
  const [screenInfo, setScreenInfo] = useState({});

  useEffect(() => {
    // Get initial detection results
    const results = testMobileDetection();
    setDetectionResults(results);
    
    // Get screen information
    setScreenInfo({
      width: window.innerWidth,
      height: window.innerHeight,
      userAgent: navigator.userAgent
    });

    // Listen for window resize events
    const handleResize = () => {
      const newResults = testMobileDetection();
      setDetectionResults(newResults);
      setScreenInfo({
        width: window.innerWidth,
        height: window.innerHeight,
        userAgent: navigator.userAgent
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <PhoneAndroid />;
      case 'tablet':
        return <Tablet />;
      case 'desktop':
        return <Computer />;
      default:
        return <Computer />;
    }
  };

  const getDeviceColor = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return 'primary';
      case 'tablet':
        return 'secondary';
      case 'desktop':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRedirectPath = () => {
    if (detectionResults?.isMobile) {
      return '/inventory-count-history';
    }
    return '/dashboard';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mobile Detection Demo
      </Typography>
      
      <Grid container spacing={3}>
        {/* Current Device Detection */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Device Detection
              </Typography>
              
              {detectionResults && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getDeviceIcon(detectionResults.deviceType)}
                    <Typography variant="body1">
                      Device Type: 
                    </Typography>
                    <Chip 
                      label={detectionResults.deviceType.toUpperCase()} 
                      color={getDeviceColor(detectionResults.deviceType)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2">
                    Is Mobile: {detectionResults.isMobile ? 'Yes' : 'No'}
                  </Typography>
                  
                  <Typography variant="body2">
                    Is Tablet: {detectionResults.isTablet ? 'Yes' : 'No'}
                  </Typography>
                  
                  <Typography variant="body2">
                    Should Use Mobile UI: {detectionResults.shouldUseMobileUI ? 'Yes' : 'No'}
                  </Typography>
                  
                  <Typography variant="body2" color="primary">
                    Redirect Path: {getRedirectPath()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Screen Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Screen Information
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  Width: {screenInfo.width}px
                </Typography>
                <Typography variant="body2">
                  Height: {screenInfo.height}px
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  User Agent: {screenInfo.userAgent?.substring(0, 50)}...
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Test Buttons */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Functions
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    const results = testMobileDetection();
                    setDetectionResults(results);
                  }}
                >
                  Run Detection Test
                </Button>
                
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    testScreenSizes();
                  }}
                >
                  Test Screen Sizes
                </Button>
                
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => {
                    const path = getRedirectPath();
                    window.location.hash = path;
                  }}
                >
                  Test Redirect to {getRedirectPath()}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              Testing Instructions
            </Typography>
            <Typography variant="body2" component="div">
              <ol>
                <li>Open browser developer tools (F12)</li>
                <li>Toggle device toolbar (Ctrl+Shift+M)</li>
                <li>Select different device types (mobile, tablet, desktop)</li>
                <li>Refresh the page to see how the detection changes</li>
                <li>Try logging in to see the redirection behavior</li>
                <li>Check the console for detailed test results</li>
              </ol>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MobileDetectionDemo;